/**
 * Casa del Bacalao · carrito vacío útil (snippets/casa-carrito-vacio.liquid).
 *
 * "Lo que viste hace poco": ids de productos que Horizon guarda en localStorage ('viewedProducts',
 *   assets/recently-viewed-products.js, máx. 4, el más reciente primero). Se resuelven en UNA
 *   petición a la búsqueda predictiva: /search/suggest.json?q=id:1 OR id:2 … (mismo truco id: que
 *   usa Horizon en predictive-search.js). Se respeta el orden de lo visto.
 * "Lo más pedido": /collections/all?sort_by=best-selling&view=casa-top (templates/collection.casa-top
 *   .liquid) devuelve los handles DISPONIBLES en el orden de ventas de Shopify. Se quitan los ya
 *   vistos y se piden a /products/<handle>.js. Si la fuente falla, la lista curada del snippet
 *   (data-respaldo), también filtrada por disponibilidad. Sin datos, el bloque no aparece.
 *
 * Dinero en centavos enteros. Formato $1,428 (decimales solo si hay centavos).
 */

const CLAVE_TOP = 'casa:mas-vendidos:lista';
const VIGENCIA_MS = 60 * 60 * 1000;
const CLAVE_VISTOS = 'viewedProducts';

const w = /** @type {any} */ (window);
const raiz = () => w.Shopify?.routes?.root || '/';

/** @param {number} centavos */
export function dinero(centavos) {
  const c = Math.round(centavos);
  const decimales = c % 100 ? 2 : 0;
  return '$' + (c / 100).toLocaleString('es-MX', { minimumFractionDigits: decimales, maximumFractionDigits: decimales });
}

/**
 * "389.00" / "92.5" / 389 → centavos enteros, sin pasar por flotantes.
 * @param {string | number | null | undefined} valor
 */
export function aCentavos(valor) {
  if (valor === null || valor === undefined || valor === '') return null;
  const s = String(valor).replace(/[^\d.]/g, '');
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  const [enteros, fraccion = ''] = s.split('.');
  return parseInt(enteros, 10) * 100 + parseInt((fraccion + '00').slice(0, 2), 10);
}

/** @type {Record<string, string> | null} */
let textosCache = null;
/**
 * @param {string} clave
 * @param {Record<string, string>} [vars]
 */
function t(clave, vars = {}) {
  if (!textosCache) {
    const nodo = document.querySelector('script[data-casa-vacio-textos]');
    if (!nodo) return ''; // aún no llega al DOM: no se cachea el vacío
    try {
      textosCache = JSON.parse(nodo.textContent || '{}');
    } catch {
      textosCache = {};
    }
  }
  let s = (textosCache && textosCache[clave]) || '';
  for (const [k, v] of Object.entries(vars)) s = s.split(`[${k}]`).join(v);
  return s;
}

/**
 * @typedef {{ handle: string, titulo: string, url: string, imagen: string, alt: string,
 *   centavos: number | null, varia: boolean, disponible: boolean }} Tarjeta
 */

/** @param {string} src @param {number} ancho */
function imagenAncho(src, ancho) {
  if (!src) return '';
  try {
    const u = new URL(src.startsWith('//') ? `https:${src}` : src, location.href);
    u.searchParams.set('width', String(ancho));
    return u.toString();
  } catch {
    return src;
  }
}

/** @param {string} handle */
function urlProducto(handle) {
  return `${raiz()}products/${encodeURIComponent(handle)}`;
}

/* ── Vistos ─────────────────────────────────────────────────────────── */

/** @returns {Promise<string[]>} */
async function idsVistos() {
  try {
    const m = await import('@theme/recently-viewed-products');
    const ids = m.RecentlyViewed.getProducts();
    if (Array.isArray(ids)) return ids.map(String);
  } catch {
    /* sin importmap o sin almacenamiento: se intenta directo */
  }
  try {
    const ids = JSON.parse(localStorage.getItem(CLAVE_VISTOS) || '[]');
    return Array.isArray(ids) ? ids.map(String) : [];
  } catch {
    return [];
  }
}

/**
 * Normaliza un producto de /search/suggest.json.
 * @param {any} p
 * @returns {Tarjeta}
 */
export function deSugerencia(p) {
  const min = aCentavos(p.price_min ?? p.price);
  const max = aCentavos(p.price_max ?? p.price);
  const img = p.featured_image || p.image || {};
  return {
    handle: String(p.handle || ''),
    titulo: String(p.title || ''),
    url: urlProducto(String(p.handle || '')),
    imagen: typeof img === 'string' ? img : img.url || '',
    alt: (typeof img === 'object' && img.alt) || String(p.title || ''),
    centavos: min,
    varia: min !== null && max !== null && max !== min,
    disponible: Boolean(p.available),
  };
}

/** @returns {Promise<Tarjeta[]>} */
async function cargarVistos() {
  const ids = (await idsVistos()).filter((id) => /^\d+$/.test(id));
  if (!ids.length) return [];
  const base = w.Theme?.routes?.predictive_search_url || `${raiz()}search/suggest`;
  const q = new URLSearchParams({
    q: ids.map((id) => `id:${id}`).join(' OR '),
    'resources[type]': 'product',
    'resources[limit]': '10',
    'resources[options][unavailable_products]': 'show',
  });
  try {
    const r = await fetch(`${base}.json?${q}`, { headers: { Accept: 'application/json' } });
    if (!r.ok) return [];
    const datos = await r.json();
    const productos = datos?.resources?.results?.products;
    if (!Array.isArray(productos)) return [];
    const porId = new Map(productos.map((p) => [String(p.id), p]));
    return ids.map((id) => porId.get(id)).filter(Boolean).map(deSugerencia);
  } catch {
    return [];
  }
}

/* ── Lo más pedido ──────────────────────────────────────────────────── */

/** @type {Promise<string[] | null> | null} */
let topEnVuelo = null;
/** Handles disponibles en orden de ventas; null si la fuente real falló */
function handlesTop() {
  if (topEnVuelo) return topEnVuelo;
  try {
    const guardado = JSON.parse(sessionStorage.getItem(CLAVE_TOP) || 'null');
    if (guardado && Date.now() - guardado.t < VIGENCIA_MS && Array.isArray(guardado.h) && guardado.h.length) {
      topEnVuelo = Promise.resolve(guardado.h);
      return topEnVuelo;
    }
  } catch {
    /* sin almacenamiento */
  }
  // Sin encabezado Accept: con 'application/json' Shopify responde otra cosa y la lista llega vacía
  topEnVuelo = fetch(`${raiz()}collections/all?sort_by=best-selling&view=casa-top`)
    .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
    .then((texto) => {
      const lista = JSON.parse(texto.trim());
      if (!Array.isArray(lista)) throw new Error('formato');
      const handles = lista.filter((h) => typeof h === 'string' && h);
      if (!handles.length) return null;
      try {
        sessionStorage.setItem(CLAVE_TOP, JSON.stringify({ t: Date.now(), h: handles }));
      } catch {
        /* nada */
      }
      return handles;
    })
    .catch(() => {
      topEnVuelo = null; // que el siguiente intento vuelva a preguntar
      return null;
    });
  return topEnVuelo;
}

/** @type {Map<string, Promise<Tarjeta | null>>} */
const productosCache = new Map();
/**
 * /products/<handle>.js → Tarjeta (precios ya vienen en centavos enteros).
 * @param {string} handle
 */
function producto(handle) {
  if (!productosCache.has(handle)) {
    productosCache.set(
      handle,
      fetch(`${urlProducto(handle)}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => (p ? deProductoJs(p) : null))
        .catch(() => null)
    );
  }
  return /** @type {Promise<Tarjeta | null>} */ (productosCache.get(handle));
}

/**
 * @param {any} p
 * @returns {Tarjeta}
 */
export function deProductoJs(p) {
  const min = Number.isInteger(p.price_min) ? p.price_min : Number.isInteger(p.price) ? p.price : null;
  const max = Number.isInteger(p.price_max) ? p.price_max : min;
  const img = p.featured_image || (Array.isArray(p.images) && p.images[0]) || '';
  return {
    handle: String(p.handle || ''),
    titulo: String(p.title || ''),
    url: urlProducto(String(p.handle || '')),
    imagen: typeof img === 'string' ? img : img?.src || '',
    alt: (p.media && p.media[0] && p.media[0].alt) || String(p.title || ''),
    centavos: min,
    varia: Boolean(p.price_varies) || (min !== null && max !== null && max !== min),
    disponible: Boolean(p.available),
  };
}

/**
 * Los primeros `max` disponibles de la lista, sin los excluidos. Pide de a poco para no disparar
 * de más: el primer lote es del tamaño justo y solo se piden más si alguno ya no está disponible.
 * @param {string[]} handles
 * @param {Set<string>} excluir
 * @param {number} max
 * @param {(h: string) => Promise<Tarjeta | null>} [obtener]
 * @returns {Promise<Tarjeta[]>}
 */
export async function elegirDisponibles(handles, excluir, max, obtener = producto) {
  const candidatos = [...new Set(handles)].filter((h) => !excluir.has(h));
  /** @type {Tarjeta[]} */
  const elegidos = [];
  let i = 0;
  while (elegidos.length < max && i < candidatos.length) {
    const lote = candidatos.slice(i, i + (max - elegidos.length));
    i += lote.length;
    const tarjetas = await Promise.all(lote.map((h) => obtener(h)));
    for (const tj of tarjetas) if (tj && tj.disponible && elegidos.length < max) elegidos.push(tj);
  }
  return elegidos;
}

/* ── Pintado ────────────────────────────────────────────────────────── */

/** @param {Tarjeta} tj */
function nodoTarjeta(tj) {
  const li = document.createElement('li');
  const a = document.createElement('a');
  a.className = 'casa-vacio__tarjeta';
  a.href = tj.url;

  if (tj.imagen) {
    const img = document.createElement('img');
    img.className = 'casa-vacio__foto';
    img.src = imagenAncho(tj.imagen, 240);
    img.srcset = `${imagenAncho(tj.imagen, 240)} 240w, ${imagenAncho(tj.imagen, 400)} 400w`;
    img.sizes = '(min-width: 750px) 180px, 45vw';
    img.width = 240;
    img.height = 240;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.alt = ''; // el nombre del producto ya es el texto del enlace
    a.append(img);
  }

  const nombre = document.createElement('span');
  nombre.className = 'casa-vacio__nombre';
  nombre.textContent = tj.titulo;
  a.append(nombre);

  if (!tj.disponible) {
    const agotado = document.createElement('span');
    agotado.className = 'casa-vacio__agotado';
    agotado.textContent = t('agotado');
    a.append(agotado);
  } else if (tj.centavos !== null) {
    const precio = document.createElement('span');
    precio.className = 'casa-vacio__precio casa-num';
    precio.textContent = tj.varia ? t('desde', { monto: dinero(tj.centavos) }) || dinero(tj.centavos) : dinero(tj.centavos);
    a.append(precio);
  }

  li.append(a);
  return li;
}

/* ── Elemento ───────────────────────────────────────────────────────── */

/** Un solo cálculo por página: el drawer puede re-crear el vacío varias veces */
/** @type {Promise<{ vistos: Tarjeta[], top: Tarjeta[] }> | null} */
let datosEnVuelo = null;

/** @param {number} max @param {string[]} respaldo */
function datos(max, respaldo) {
  if (!datosEnVuelo) {
    datosEnVuelo = (async () => {
      const [vistos, top] = await Promise.all([cargarVistos(), handlesTop()]);
      const vistosMostrar = vistos.slice(0, max);
      const excluir = new Set(vistosMostrar.map((v) => v.handle));
      let elegidos = top ? await elegirDisponibles(top, excluir, max) : [];
      if (!elegidos.length && respaldo.length) elegidos = await elegirDisponibles(respaldo, excluir, max);
      return { vistos: vistosMostrar, top: elegidos };
    })().finally(() => {
      // Vale para esta visita al vacío; si el cliente vuelve a vaciar el carrito más tarde, se recalcula
      setTimeout(() => (datosEnVuelo = null), 60 * 1000);
    });
  }
  return datosEnVuelo;
}

class CasaCarritoVacio extends HTMLElement {
  connectedCallback() {
    const max = Math.max(1, Number(this.dataset.max) || 4);
    const respaldo = (this.dataset.respaldo || '')
      .split(',')
      .map((h) => h.trim())
      .filter(Boolean);
    datos(max, respaldo).then(({ vistos, top }) => {
      if (!this.isConnected) return;
      this.#llenar('vistos', vistos);
      this.#llenar('top', top);
    });
  }

  /**
   * @param {'vistos' | 'top'} nombre
   * @param {Tarjeta[]} tarjetas
   */
  #llenar(nombre, tarjetas) {
    const bloque = /** @type {HTMLElement | null} */ (this.querySelector(`[data-bloque="${nombre}"]`));
    const lista = bloque?.querySelector('[data-lista]');
    if (!bloque || !lista) return;
    lista.replaceChildren(...tarjetas.map(nodoTarjeta));
    bloque.hidden = tarjetas.length === 0;
  }
}

if (!customElements.get('casa-carrito-vacio')) customElements.define('casa-carrito-vacio', CasaCarritoVacio);
